import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ColorPalette } from '../../styles/constants';
import { Activity } from '../../models/activity';
import { formatTableViewDate } from '../../services/dateService';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import { AppActivityTableRowMobileActions } from './appActivityTableRowMobileActions';
import { isWeekend } from 'date-fns';
import { TARGET_FULL_TIME_HOURS } from '../../constants';
import { generateProjectLabel } from '../../services/projectService';

const styles = {
  workedTimeWarning: {
    color: ColorPalette.WARNING_RED,
  },
  workedTimeProjects: {
    fontWeight: 600,
  },
  actionsBtn: {
    color: ColorPalette.DARK_GRAY,
  },
  projectName: {
    fontWeight: 500,
    borderRight: `1px solid ${ColorPalette.LESS_LIGHT_GRAY}`,
    fontSize: 16,
  },
  rowText: {
    fontWeight: 500,
    borderRight: `1px solid ${ColorPalette.LESS_LIGHT_GRAY}`,
    fontSize: 14,
  },
  infoRow: {
    background: ColorPalette.LIGHT_GRAY,
    padding: 0,
  },
  sColumn: {
    width: '10%',
  },
  mColumn: {
    width: '20%',
  },
  umbrellaIcon: {
    ml: 0.5,
    fontSize: '1.2rem',
    color: ColorPalette.DARK_GRAY,
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  activityHoursMobile: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
};

interface ActivityViewProps {
  activityList: Activity[];
  activityDate: string;
  isDayOff: boolean;
  isDayBeforeDayOff: boolean;
  isEmployeeSection?: boolean;
  employeeWorkHours?: number;
  hasAMSActivities?: boolean;
  handleEditActivity: (activity: Activity) => void;
  handleDeleteActivity: (activity: Activity) => void;
  handleAddActivity: (addDate: string) => void;
}

export function AppActivityTableRow({
  activityList,
  activityDate,
  isDayOff,
  isDayBeforeDayOff,
  isEmployeeSection,
  hasAMSActivities,
  employeeWorkHours,
  handleEditActivity,
  handleDeleteActivity,
  handleAddActivity,
}: ActivityViewProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formattedDate = useMemo(() => {
    return formatTableViewDate(activityDate);
  }, [activityDate]);

  const totalHoursLogged = useMemo(() => {
    return activityList.reduce(
      (loggedHours, activity) => (loggedHours += activity.hours),
      0
    );
  }, [activityList]);

  const dayWorkingHours = useMemo(() => {
    const workingHours = employeeWorkHours || TARGET_FULL_TIME_HOURS;

    if (workingHours === TARGET_FULL_TIME_HOURS && isDayBeforeDayOff)
      return workingHours - 1;

    return workingHours;
  }, [employeeWorkHours, isDayBeforeDayOff]);

  const isWeekendOrDayOff = useMemo(() => {
    return isWeekend(Date.parse(activityDate)) || isDayOff;
  }, [activityDate, isDayOff]);

  const handleEditOptionMenu = useCallback(
    (activity) => {
      handleEditActivity(activity);
    },
    [handleEditActivity]
  );

  const handleDeleteOptionMenu = useCallback(
    (activity) => {
      handleDeleteActivity(activity);
    },
    [handleDeleteActivity]
  );

  return (
    <React.Fragment>
      <TableRow>
        <TableCell colSpan={7} sx={styles.infoRow}>
          <Grid container alignItems="center">
            <Grid item xs container alignItems="center">
              {isEmployeeSection && (
                <Grid
                  item
                  sx={[styles.sColumn]}
                  container
                  justifyContent="center"
                >
                  <IconButton
                    sx={styles.actionsBtn}
                    style={{ marginLeft: 4 }}
                    onClick={() => handleAddActivity(activityDate)}
                  >
                    <AddIcon />
                  </IconButton>
                </Grid>
              )}
              <Typography
                variant="h6"
                style={{ marginRight: 16, marginLeft: 16 }}
              >
                {formattedDate}
              </Typography>

              {isEmployeeSection &&
                (isWeekendOrDayOff ? (
                  isDayOff && totalHoursLogged === 0 ? (
                    <BeachAccessOutlinedIcon sx={styles.umbrellaIcon} />
                  ) : (
                    <Typography variant="h6" sx={styles.workedTimeWarning}>
                      (Logged: {totalHoursLogged}/0 h)
                    </Typography>
                  )
                ) : totalHoursLogged !== dayWorkingHours ? (
                  <Typography variant="h6" sx={styles.workedTimeWarning}>
                    (Logged: {totalHoursLogged}/{dayWorkingHours} h)
                  </Typography>
                ) : null)}

              {!isEmployeeSection && (
                <Typography variant="h6" sx={styles.workedTimeProjects}>
                  (Logged: {totalHoursLogged}h)
                </Typography>
              )}
            </Grid>
          </Grid>
        </TableCell>
      </TableRow>
      {activityList.length > 0 &&
        activityList.map((activity) => (
          <TableRow key={activity.id}>
            {isEmployeeSection && !isMobile && (
              <TableCell align="center" sx={[styles.rowText, styles.sColumn]}>
                <Box style={{ marginLeft: 8 }}>
                  <IconButton onClick={() => handleEditActivity(activity)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteActivity(activity)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </TableCell>
            )}
            {!isMobile && <TableCell sx={{ ...styles.sColumn }}></TableCell>}
            <TableCell align="left" sx={[styles.projectName, styles.mColumn]}>
              {isEmployeeSection
                ? generateProjectLabel(
                    activity.task.project.name,
                    activity.task.project.code
                  )
                : activity.employee.name}
            </TableCell>
            <TableCell align="left" sx={[styles.rowText, styles.mColumn]}>
              {activity.task.name}
            </TableCell>
            {!isMobile && hasAMSActivities && (
              <TableCell align="left" sx={[styles.rowText, styles.sColumn]}>
                {activity.ticketIdentification}
              </TableCell>
            )}
            <TableCell align="left" sx={[styles.rowText, styles.sColumn]}>
              {isMobile && isEmployeeSection ? (
                <Box sx={styles.actionsContainer}>
                  <Box sx={styles.activityHoursMobile}>
                    <Typography> {activity.hours}</Typography>
                  </Box>
                  <AppActivityTableRowMobileActions
                    activity={activity}
                    handleEditActivity={handleEditOptionMenu}
                    handleDeleteActivity={handleDeleteOptionMenu}
                  />
                </Box>
              ) : (
                <Typography> {activity.hours}</Typography>
              )}
            </TableCell>
            {!isMobile && (
              <TableCell align="left" sx={[styles.rowText, styles.mColumn]}>
                {activity.comment}
              </TableCell>
            )}
          </TableRow>
        ))}
    </React.Fragment>
  );
}
